import React from "react";
import VariableArrayRow from "./VariableArrayRow";
import { Button, Col, Input, Label, Row } from "reactstrap";

/**
 * Content-mode Array:content building block (SCRUM-643) — the
 * name/description-pairs editor, extracted verbatim from the content
 * screens. The settings side keeps the plain ArrayContentField.
 *
 * Variants (per-surface chrome, both pinned by SCRUM-630/640):
 * - `update` (default; HandleContent (both statuses), character-identical in
 *   both): collapsible `<details className="content-array-container mb-3"
 *   open>` with the variable name as `<summary>`; items fallback WITHOUT a
 *   length check (`[]` renders zero items); removeItem unguarded (the button
 *   is disabled at one item).
 * - `create` (the wizard columns): plain `<div className=
 *   "content-array-container mb-3">` with a Label header row (`summaryProps`
 *   carries the wizard's drag-handle passthrough); items fallback WITH a
 *   length check (`[]` renders one blank pair); removeItem guarded at one.
 *
 * Emits the full [{name, description}] via `onChange(name, value)`.
 */
export type ContentArrayItem = { name?: string; description?: string };

export type ContentArrayContentFieldProps = {
  settingVar: { variable?: string; value?: unknown };
  onChange: (name: string | undefined, value: ContentArrayItem[]) => void;
  variant?: "create" | "update";
  summaryProps?: Record<string, unknown>;
};

export default function ContentArrayContentField({
  settingVar,
  onChange,
  variant = "update",
  summaryProps = {},
}: ContentArrayContentFieldProps) {
  const fieldName = settingVar.variable;
  const isCreate = variant === "create";
  const items: ContentArrayItem[] = isCreate
    ? Array.isArray(settingVar.value) && settingVar.value.length > 0
      ? settingVar.value
      : [{ name: "", description: "" }]
    : Array.isArray(settingVar.value)
      ? settingVar.value
      : [{ name: "", description: "" }];

  const setItems = (next: ContentArrayItem[]) => onChange(fieldName, next);

  const updateItem =
    (idx: number, field: keyof ContentArrayItem) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    setItems(
      items.map((item, i) => (i === idx ? { ...item, [field]: newVal } : item))
    );
  };

  const addItem = () => setItems([...items, { name: "", description: "" }]);
  const removeItem = (idx: number) => {
    if (isCreate && items.length <= 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  const descriptionArea = (i: number, text: string) => ({
    rows: 4,
    placeholder: "Enter description…",
    value: text,
    onChange: updateItem(i, "description"),
    name: `${fieldName}[${i}].description`,
  });

  const body = (
    <>
      {items.map((item, i) => (
        // index key: controlled row, no row-local state (SCRUM-2183)
        <VariableArrayRow key={i} className="content-item mb-3" topRowClassName="mb-2 varible_container"
          topLabel={`Name #${i + 1}`} onRemove={() => removeItem(i)} removeDisabled={items.length === 1}
          bottomRowClassName="varible_container" bottomLabel={`Description #${i + 1}`}
          textarea={descriptionArea(i, item.description || "")}>
          <Col md="9">
            <Input type="text" placeholder="Enter name…" value={item.name || ""}
              onChange={updateItem(i, "name")} name={`${fieldName}[${i}].name`} />
          </Col>
        </VariableArrayRow>
      ))}
      <Row className="mt-3">
        <Col md={{ offset: 2, size: 10 }}>
          <Button type="button" color="primary" onClick={addItem}>
            + Add content item
          </Button>
        </Col>
      </Row>
    </>
  );

  if (isCreate) {
    return (
      <div className="content-array-container mb-3">
        <Row className="varible_container mb-3">
          <Label md="2" className="form-control-label" {...summaryProps}>
            {fieldName}
          </Label>
        </Row>
        {body}
      </div>
    );
  }

  return (
    <details className="content-array-container mb-3" open>
      <summary
        className="varible_container form-control-label d-flex align-items-center"
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        {fieldName}
      </summary>
      <div className="pt-3">{body}</div>
    </details>
  );
}

// renders its own header (summary / label row) — the dispatcher skips its label row
ContentArrayContentField.ownsChrome = true;
